/**
 * Cash Flows hub page registry — Access `frmReportCashFlowMain` "Go to Page"
 * combo (cbPreferencesList) + per-page toolbar config (cbPreferencesList_Change).
 *
 * Each page maps to a list view + the toolbar buttons the Access form shows for
 * that page. Button captions/visibility copied verbatim from the VBA.
 */
import type { CashflowColumn } from './CashflowDataList'

export type CashflowPageKey =
  | 'load_on_board'
  | 'project_new_p'
  | 'project_new_npe'
  | 'others'
  | 'bank'
  | 'cp'
  | 'service'
  | 'muahang'
  | 'banhang'

/** Toolbar action ids → dialog the Frame opens. */
export type CashflowAction =
  | 'input'
  | 'cp-edit'
  | 'cp-need'
  | 'cp-dup'
  | 'cp-misa'
  | 'svc-edit'
  | 'svc-need'
  | 'svc-dup'
  | 'svc-misa'
  | 'mh-new'
  | 'mh-edit'
  | 'mh-delete'
  | 'mh-need'
  | 'mh-misa'
  | 'bh-new'
  | 'bh-edit'
  | 'bh-delete'
  | 'bh-need'
  | 'bh-misa'

export interface ToolbarButtonCfg {
  action: CashflowAction
  /** Short display label (shown on the button). */
  label: string
  /**
   * Discriminator shown on xs (mobile) instead of hiding the label to icon-only.
   * Required for buttons that share the same icon (e.g. all `list` mapping
   * buttons) so they stay distinguishable on mobile (responsive Pattern 10).
   */
  shortLabel?: string
  /** Full Access caption — used as tooltip when `label` is abbreviated. */
  title?: string
  color: 'success' | 'warning' | 'primary' | 'error' | 'secondary'
  icon: 'new' | 'edit' | 'delete' | 'list' | 'value'
  rowRequired: boolean
}

export interface CashflowPageCfg {
  key: CashflowPageKey
  label: string
  group: 'Input' | 'Report' | 'Mapping'
  buttons: ToolbarButtonCfg[]
}

/** Combo options in Access RowSource order. */
export const CASHFLOW_PAGES: CashflowPageCfg[] = [
  {
    key: 'load_on_board',
    label: 'Input - Loading on Board',
    group: 'Input',
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: false }],
  },
  {
    key: 'project_new_p',
    label: 'Input - Projected New Purchases',
    group: 'Input',
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: false }],
  },
  {
    key: 'project_new_npe',
    label: 'Input - Projected New NPE',
    group: 'Input',
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: false }],
  },
  {
    key: 'others',
    label: 'Input - Others',
    group: 'Input',
    // rowRequired: each Others row IS its own input_type → must pick a row so the
    // save targets the right input_type (not a blank key).
    buttons: [{ action: 'input', label: 'Input', title: 'Input Value', color: 'success', icon: 'value', rowRequired: true }],
  },
  { key: 'bank', label: 'Report - Balance Bank Account', group: 'Report', buttons: [] },
  {
    key: 'cp',
    label: 'Customer/Vender Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'cp-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'cp-need', label: 'Need Map', shortLabel: 'Need', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'cp-dup', label: 'Duplicate', shortLabel: 'Dup', title: 'Duplicate Data', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'cp-misa', label: 'Misa CP', shortLabel: 'Misa CP', title: 'Misa Vender/Customer', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
  {
    key: 'service',
    label: 'Service Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'svc-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'svc-need', label: 'Need Map', shortLabel: 'Need', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'svc-dup', label: 'Duplicate', shortLabel: 'Dup', title: 'Duplicate Data', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'svc-misa', label: 'Misa CP', shortLabel: 'Misa CP', title: 'Misa Vender/Customer', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
  {
    key: 'muahang',
    label: 'Purchase - Invoice Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'mh-new', label: 'New', title: 'New - Mapping', color: 'success', icon: 'new', rowRequired: false },
      { action: 'mh-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'mh-delete', label: 'Delete', title: 'Delete - Mapping', color: 'error', icon: 'delete', rowRequired: true },
      { action: 'mh-need', label: 'Need Map', shortLabel: 'Need', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'mh-misa', label: 'Misa Purchase', shortLabel: 'Misa Pur', title: 'Misa Purchase Invoices', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
  {
    key: 'banhang',
    label: 'Sale - Invoice Mapping',
    group: 'Mapping',
    buttons: [
      { action: 'bh-new', label: 'New', title: 'New - Mapping', color: 'success', icon: 'new', rowRequired: false },
      { action: 'bh-edit', label: 'Edit', title: 'Edit - Mapping', color: 'warning', icon: 'edit', rowRequired: true },
      { action: 'bh-delete', label: 'Delete', title: 'Delete - Mapping', color: 'error', icon: 'delete', rowRequired: true },
      { action: 'bh-need', label: 'Need Map', shortLabel: 'Need', title: 'Need to map', color: 'primary', icon: 'list', rowRequired: false },
      { action: 'bh-misa', label: 'Misa Sales', shortLabel: 'Misa Sale', title: 'Misa Sales Invoices', color: 'primary', icon: 'list', rowRequired: false },
    ],
  },
]

/**
 * Hub split: the "Cash Flows" tab shows the Input pages + the Balance Bank
 * Account report; the "Misa Mapping" tab shows the four MISA mapping pages.
 */
export const CASHFLOW_HUB_PAGES: CashflowPageCfg[] = CASHFLOW_PAGES.filter((p) => p.group !== 'Mapping')
export const MISA_MAPPING_PAGES: CashflowPageCfg[] = CASHFLOW_PAGES.filter((p) => p.group === 'Mapping')

// ── Column definitions per list (Access ColumnOrder; captions verbatim). ──
export const BANK_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'bankAccountNumber', label: 'Bank Number', width: 150 },
  { key: 'bankName', label: 'Bank Name' , wrap: true, width: 250 },
  { key: 'openAmountOc', label: 'Open (Raw)', kind: 'number', align: 'right', width: 150 },
  { key: 'openAmount', label: 'Open (VND)', kind: 'number', align: 'right', width: 150 },
  { key: 'debitAmountOc', label: 'Debit (Raw)', kind: 'number', align: 'right', width: 150 },
  { key: 'debitAmount', label: 'Debit (VND)', kind: 'number', align: 'right', width: 150 },
  { key: 'creditAmountOc', label: 'Credit (Raw)', kind: 'number', align: 'right', width: 135 },
  { key: 'creditAmount', label: 'Credit (VND)', kind: 'number', align: 'right', width: 135 },
  { key: 'closeAmountOc', label: 'Close (Raw)', kind: 'number', align: 'right', width: 150 },
  { key: 'closeAmount', label: 'Close (VND)', kind: 'number', align: 'right', width: 150 },
  { key: 'bankFx', label: 'Bank FX', kind: 'number', align: 'right', width: 105 },
  { key: 'bankBranchName', label: 'Bank Branch Name' , wrap: true, width: 180 },
  { key: 'isSudung', label: 'Is Used?', filter: 'checkboxes', width: 120 },
  { key: 'description', label: 'Description' , wrap: true, width: 220 },
  { key: 'address', label: 'Address' , wrap: true, width: 250 },
]

// Column order = Access datasheet `frmReportCashFlowQryCPMapping_result` TabIndex
// (ColumnOrder all 0 → fallback TabIndex per project convention).
// Default widths: text cols (short/full name) wider + wrap; value cols (code /
// VAT / Yes-No flags) sized to fit the header label (the value itself is short).
export const CP_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMap', label: 'IsMapped?', filter: 'checkboxes', width: 110 },
  { key: 'onshoreOffshore', label: 'ERP - Onshore/Offshore', filter: 'checkboxes', width: 175 },
  { key: 'erpCode', label: 'ERP - Code', width: 120 },
  { key: 'erpShortName', label: 'ERP - ShortName' , wrap: true, width: 180, proper: true },
  { key: 'hadPcontract', label: 'ERP - Had P contract?', filter: 'checkboxes', width: 175 },
  { key: 'hadScontract', label: 'ERP - Had S contract?', filter: 'checkboxes', width: 175 },
  { key: 'erpVat', label: 'ERP - VATCode', width: 125 },
  { key: 'misaCode', label: 'MISA - Code', wrap: true, width: 180 },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 200, proper: true },
  { key: 'erpName', label: 'ERP - Name' , wrap: true, width: 200, proper: true },
  { key: 'misaVat', label: 'MISA - VATCode', width: 125 },
  { key: 'isVender', label: 'MISA - IsVender?', filter: 'checkboxes', width: 140 },
  { key: 'isCustomer', label: 'MISA - IsCustomer?', filter: 'checkboxes', width: 155 },
  { key: 'isEmployee', label: 'MISA - IsEmployee?', filter: 'checkboxes', width: 155 },
]

// "Need Map" viewer — Access datasheet `frmReportCashFlowQryCPMapping_result_need`
// has its OWN TabIndex order + captions (NOT the main-list order); mirror it 1:1.
export const CP_NEED_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMap', label: 'IsMapped?', filter: 'checkboxes' },
  { key: 'checkType', label: 'Object Type', filter: 'checkboxes' },
  { key: 'erpCode', label: 'ERP - Code' },
  { key: 'erpShortName', label: 'ERP - ShortName' , wrap: true, width: 180, proper: true },
  { key: 'hadPcontract', label: 'ERP - Had P contract?', filter: 'checkboxes' },
  { key: 'hadScontract', label: 'ERP - Had S contract?', filter: 'checkboxes' },
  { key: 'erpVat', label: 'ERP - VATCode' },
  { key: 'misaCode', label: 'MISA - Code' },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 200, proper: true },
  { key: 'misaNgoaile', label: 'OBJECT - Note' },
  { key: 'erpName', label: 'ERP - Name' , wrap: true, width: 200, proper: true },
  { key: 'misaVat', label: 'MISA - VATCode' },
  { key: 'misaLstInvoice', label: 'MISA - LstInvoices' , wrap: true, width: 200 },
  { key: 'isVender', label: 'MISA - IsVender?', filter: 'checkboxes' },
  { key: 'isCustomer', label: 'MISA - IsCustomer?', filter: 'checkboxes' },
  { key: 'isEmployee', label: 'MISA - IsEmployee?', filter: 'checkboxes' },
]

// "Duplicate Data" viewer — Access `frmReportCashFlowQryCPMapping_result_dup`
// shows only 7 visible columns (had_scontract is ColumnHidden=True → omitted),
// own TabIndex order + captions. NOT the full main-list column set.
export const CP_DUP_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'erpCode', label: 'ERP - Code' },
  { key: 'erpShortName', label: 'ERP - ShortName' , wrap: true, width: 180, proper: true },
  { key: 'erpVat', label: 'ERP - VATCode' },
  { key: 'hadPcontract', label: 'Is Had PContract?', filter: 'checkboxes' },
  { key: 'misaCode', label: 'MISA - Code' },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 200, proper: true },
  { key: 'misaVat', label: 'MISA - VATCode' },
]

export const SERVICE_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes', width: 110 },
  { key: 'serviceCode', label: 'ServiceCode', width: 120 },
  { key: 'serviceTypeName', label: 'ServiceType' , wrap: true, width: 160 },
  { key: 'serviceName', label: 'ServiceName' , wrap: true, width: 200, proper: true },
  { key: 'misaCode', label: 'MisaCode', width: 110 },
  { key: 'misaName', label: 'MisaName' , wrap: true, width: 200, proper: true },
  { key: 'misaVatCode', label: 'MisaVATCode', width: 125 },
  { key: 'tlcPic', label: 'Service PIC', width: 120 },
  { key: 'tel', label: 'Service Tel', width: 120 },
  { key: 'misaAddress', label: 'MisaAddress' , wrap: true, width: 200 },
  { key: 'address', label: 'Service Address' , wrap: true, width: 220 },
  { key: 'taxNo', label: 'Service TaxNo', width: 150 },
  { key: 'note', label: 'Service Note' , wrap: true, width: 220 },
  { key: 'misaId', label: 'MisaID', width: 110 },
]

// "Objects need to map" viewer — Access `frmReportCashFlowQryServiceMapping_result_need`
// has its OWN physical order (ColumnOrder all 0 → TabIndex fallback): identical
// column set to the base list BUT `misaAddress` sits right after `misaName`
// (before misaVATCode), not after `tel`. Mirror that order 1:1.
export const SERVICE_NEED_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes', width: 110 },
  { key: 'serviceCode', label: 'ServiceCode', width: 120 },
  { key: 'serviceTypeName', label: 'ServiceType' , wrap: true, width: 160 },
  { key: 'serviceName', label: 'ServiceName' , wrap: true, width: 200, proper: true },
  { key: 'misaCode', label: 'MisaCode', width: 110 },
  { key: 'misaName', label: 'MisaName' , wrap: true, width: 200, proper: true },
  { key: 'misaAddress', label: 'MisaAddress' , wrap: true, width: 200 },
  { key: 'misaVatCode', label: 'MisaVATCode', width: 125 },
  { key: 'tlcPic', label: 'Service PIC', width: 120 },
  { key: 'tel', label: 'Service Tel', width: 120 },
  { key: 'address', label: 'Service Address' , wrap: true, width: 220 },
  { key: 'taxNo', label: 'Service TaxNo', width: 150 },
  { key: 'note', label: 'Service Note' , wrap: true, width: 220 },
  { key: 'misaId', label: 'MisaID', width: 110 },
]

export const MISA_OBJECT_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'misaCode', label: 'MISA - Code' },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 200 },
  { key: 'misaAddress', label: 'MISA - Address' , wrap: true, width: 200 },
  { key: 'misaVatCode', label: 'MISA - VATCode' },
  { key: 'mappedTxt', label: 'Mapped?' },
  { key: 'erpCode', label: 'ERP - Code' },
  { key: 'erpName', label: 'ERP - Name' , wrap: true, width: 200 },
]

// "Misa CP" viewer — Access `frmReportCashFlowQryCPMapping_result_misa` hides
// erp_name (ColumnHidden=True). Kept separate so the Service "Misa CP" viewer
// (shared MISA_OBJECT_COLUMNS) is unaffected. Widths sized to value: code / VAT
// compact, name/address wide + wrap, mapped flag narrow.
export const CP_MISA_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'misaCode', label: 'MISA - Code', width: 120 },
  { key: 'misaName', label: 'MISA - Name' , wrap: true, width: 260, proper: true },
  { key: 'misaAddress', label: 'MISA - Address' , wrap: true, width: 320 },
  { key: 'misaVatCode', label: 'MISA - VATCode', width: 150 },
  { key: 'mappedTxt', label: 'Mapped?', filter: 'checkboxes', width: 110 },
  { key: 'erpCode', label: 'ERP - Code', width: 120 },
]

export const MUAHANG_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'mappedTxt', label: 'Is Mapped?', filter: 'checkboxes', width: 115 },
  { key: 'sohoadonCheckdate', label: 'ERP - Check Date', kind: 'date', width: 150 },
  { key: 'erpCp', label: 'ERP - CP Code', width: 150 },
  { key: 'erpMisaCode', label: 'ERP - Misa Code', wrap: true, width: 200 },
  { key: 'erpTabName', label: 'ERP - Tab Name', width: 140 },
  { key: 'erpInvNo', label: 'ERP - Invoice No', width: 145 },
  { key: 'invoiceDate', label: 'ERP - Invoice Date', kind: 'date', width: 155 },
  { key: 'invoiceAmount', label: 'ERP - Invoice Amount', kind: 'number', align: 'right', width: 175 },
  { key: 'erpInvNote', label: 'ERP - Invoice Note' , wrap: true, width: 200 },
  { key: 'tabName', label: 'Misa - Tab Name', width: 145 },
  { key: 'nhacungcap', label: 'Misa - Vender' , wrap: true, width: 200 },
  { key: 'sochungtu', label: 'Misa - Vourcher Number', width: 185 },
  { key: 'sohoadon', label: 'Misa - Invoice Number', width: 180 },
  { key: 'ngayhoadon', label: 'Misa - Invoice Date', kind: 'date', width: 160 },
  { key: 'thanhtienQuydoi', label: 'Misa - Amount (Convert)', kind: 'number', align: 'right', width: 200 },
  { key: 'thanhtoanQuydoi', label: 'Misa - Payment (Convert)', kind: 'number', align: 'right', width: 205 },
  { key: 'loaitien', label: 'Misa - Currency', width: 140 },
  { key: 'tygia', label: 'Misa - Exchange Rate', kind: 'number', align: 'right', width: 165 },
  { key: 'journalMemo', label: 'Misa - Description', wrap: true, width: 320 },
  { key: 'refid', label: 'Misa - Refid', width: 125 },
  { key: 'paydataRequested', label: 'ERP - Payment Requested', kind: 'number', align: 'right', width: 205 },
  { key: 'paydataPaid', label: 'ERP - Payment Paid', kind: 'number', align: 'right', width: 165 },
  { key: 'contractNo', label: 'ERP - Contract No', width: 150 },
]

// "Need to map" viewer — Access `frmReportCashFlowQryMuaHang_need` shows only the
// 12 ERP-side columns (no Misa columns), own ColumnOrder. NOT the full main list.
export const MUAHANG_NEED_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'mappedTxt', label: 'Is Mapped?', filter: 'checkboxes', width: 115 },
  { key: 'sohoadonCheckdate', label: 'ERP - Check Date', kind: 'date', width: 150 },
  { key: 'erpCp', label: 'ERP - CP Code', width: 150 },
  { key: 'erpMisaCode', label: 'ERP - Misa Code', width: 145 },
  { key: 'erpTabName', label: 'ERP - Tab Name', width: 140 },
  { key: 'erpInvNo', label: 'ERP - Invoice No', width: 145 },
  { key: 'invoiceDate', label: 'ERP - Invoice Date', kind: 'date', width: 155 },
  { key: 'invoiceAmount', label: 'ERP - Invoice Amount', kind: 'number', align: 'right', width: 175 },
  { key: 'erpInvNote', label: 'ERP - Invoice Note' , wrap: true, width: 200 },
  { key: 'paydataRequested', label: 'ERP - Payment Requested', kind: 'number', align: 'right', width: 205 },
  { key: 'paydataPaid', label: 'ERP - Payment Paid', kind: 'number', align: 'right', width: 165 },
  { key: 'contractNo', label: 'ERP - Contract No', width: 150 },
]

export const BANHANG_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes', width: 110 },
  { key: 'contractNo', label: 'ERP-ContractNo', width: 140 },
  { key: 'contractDate', label: 'ERP-ContractDate', kind: 'date', width: 150 },
  { key: 'erpInv', label: 'ERP-InvNo', wrap: true, width: 170 },
  { key: 'buyersRef', label: 'ERP-BuyersRef', width: 135 },
  { key: 'cpCode', label: 'ERP-CpCode', width: 120 },
  { key: 'shortName', label: 'ERP-CpName' , wrap: true, width: 220, proper: true },
  { key: 'accountObjectCode', label: 'MISA-ObjectCode', wrap: true, width: 200 },
  { key: 'tabName', label: 'MISA-Type', width: 115 },
  { key: 'sochungtu', label: 'MISA-VoucherNo', width: 145 },
  { key: 'misaContract', label: 'MISA-ContractNo', width: 150 },
  { key: 'contractExtract', label: 'MISA-ContractExtract' , wrap: true, width: 200 },
  { key: 'invExtract', label: 'MISA-InvExtract' , wrap: true, width: 200 },
  { key: 'misaSohoadon', label: 'MISA-InvNo', wrap: true, width: 180 },
  { key: 'dienGiai', label: 'MISA-Note' , wrap: true, width: 320 },
  { key: 'contractType', label: 'ERP-ContractType', width: 155 },
  // Access `frmReportCashFlowQryBanhang` #17-30 — truoc 2026-08-05 web cat cut o cot
  // #16, thieu ca khoi ERP-Inv* va MISA-*. Nguon: v_report_cashflow_map_banhang_sohoadon
  // (BE da bo sung vao _BH_COLS + MapBanHangOut).
  { key: 'erpInvDate', label: 'ERP-InvDate', kind: 'date', width: 130 },
  { key: 'erpInvPriceCustomer', label: 'ERP-InvPrice-Customer', kind: 'number', width: 175 },
  { key: 'erpInvPriceCustoms', label: 'ERP-InvPrice-Customs', kind: 'number', width: 170 },
  { key: 'erpInvAmountCustomer', label: 'ERP-InvAmount-Customer', kind: 'number', width: 185 },
  { key: 'erpInvAmountCustoms', label: 'ERP-InvAmount-Customs', kind: 'number', width: 180 },
  { key: 'ngayhachtoan', label: 'MISA-PostedDate', kind: 'date', width: 145 },
  { key: 'ngaychungtu', label: 'MISA-VoucherDate', kind: 'date', width: 150 },
  { key: 'loaitien', label: 'MISA-Currency', width: 130 },
  { key: 'tygia', label: 'MISA-FX', kind: 'number', width: 110 },
  { key: 'tongTienHang', label: 'MISA-SaleAmount', kind: 'number', width: 150 },
  { key: 'tienChietKhau', label: 'MISA-DiscountAmount', kind: 'number', width: 175 },
  { key: 'tienThueGtgt', label: 'MISA-VATAmount', kind: 'number', width: 150 },
  { key: 'tongTienThanhToan', label: 'MISA-PaymentAmount', kind: 'number', width: 170 },
  { key: 'refId', label: 'MISA-RefID', width: 130 },
]

// "Objects need to map" viewer — Access `frmReportCashFlowQryBanHang_need` shows
// 14 ERP-side columns (no MISA columns), own order + captions. Mirror 1:1.
// "Need to map" viewer — Access `frmReportCashFlowQryBanHang_need`: 9 visible
// columns (ColumnOrder 1→16; price/amount/inv-date cols are ColumnOrder=0 = not
// shown). Order matches the datasheet.
export const BANHANG_NEED_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'isMapped', label: 'IsMapped?', filter: 'checkboxes', width: 110 },
  { key: 'contractNo', label: 'ERP-ContractNo', width: 140 },
  { key: 'erpInv', label: 'ERP-InvNo', width: 120 },
  { key: 'buyersRef', label: 'ERP-BuyersRef', width: 135 },
  { key: 'cpCode', label: 'ERP-CpCode', width: 120 },
  { key: 'shortName', label: 'ERP-CpName', wrap: true, width: 180 },
  { key: 'accountObjectCode', label: 'MISA-ObjectCode', width: 150 },
  { key: 'contractDate', label: 'ERP-ContractDate', kind: 'date', width: 150 },
  { key: 'contractType', label: 'ERP-ContractType', width: 150 },
]

export const BANHANG_DIFF_COLUMNS: CashflowColumn<Record<string, unknown>>[] = [
  { key: 'customsInvNo', label: 'Customs Inv No' },
  { key: 'contractNo', label: 'Contract No' },
  { key: 'erpKhachhang', label: 'ERP - Customer' , wrap: true, width: 180 },
  { key: 'misaLstCt', label: 'MISA - Lst Contract' , wrap: true, width: 200 },
  { key: 'misaKhachhang', label: 'MISA - Customer' , wrap: true, width: 180 },
]
